import { Flex } from 'components';
import { FieldLabel, Select, TextField } from 'components/_form';
import { ColorPicker } from 'components/_form/ColorPicker/ColorPicker';
import { IOption } from 'components/_form/Select/Select';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IPlatformPracticeFormFields } from 'types/platformPractice';
import { PriorityLevelBadge } from 'views/AdministrationView/views/TasksView/components/Priorities/components/PriorityLevelBadge/PriorityLevelBadge';
import { getPlatformMethodologies } from 'api/platformMethodologies';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';

export const PlatformPracticeForm: React.FC<{
  lockedMethodology?: { id: string; name: string };
}> = ({ lockedMethodology }) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    watch,
    formState: { errors }
  } = useFormContext<IPlatformPracticeFormFields>();

  const name = watch('name');
  const codeId = watch('id_code');
  const colorCode = watch('color_code');

  const previewSource = {
    name,
    id_code: codeId,
    color_code: colorCode
  };

  const [methodologyOptions, setMethodologyOptions] = useState<IOption[]>([]);

  const stateOptions: IOption[] = useMemo(
    () => [
      { label: t('platformPractices.states.active'), value: 'active' },
      { label: t('platformPractices.states.inactive'), value: 'inactive' },
      { label: t('platformPractices.states.draft'), value: 'draft' }
    ],
    [t]
  );

  useEffect(() => {
    if (lockedMethodology) {
      setMethodologyOptions([
        { label: lockedMethodology.name, value: lockedMethodology.id }
      ]);
      return;
    }

    let cancelled = false;

    const loadMethodologies = async () => {
      try {
        const response = await getPlatformMethodologies();

        if (cancelled) return;

        const options = response.data.map((methodology) => ({
          label: methodology.name,
          value: String(methodology.id)
        }));

        setMethodologyOptions(options);
      } catch (error) {
        if (!cancelled) {
          showPlatformApiError(error, t);
        }
      }
    };

    loadMethodologies();

    return () => {
      cancelled = true;
    };
  }, [lockedMethodology, t]);

  return (
    <Flex flexDirection="column" gap="16px">
      <Flex alignItems="center" flexDirection={['column', 'row']}>
        <Flex width={['100%', '30%']}>
          <FieldLabel>{t('platformPractices.clickToChooseColor')}</FieldLabel>
        </Flex>
        <Flex
          width={['100%', '70%']}
          gap="16px"
          alignItems="center"
          flexWrap="wrap"
        >
          <Controller
            control={control}
            name="color_code"
            render={({ field: { value, onChange } }) => (
              <ColorPicker controlledDisplayColorPicker={value} onChange={onChange} />
            )}
          />
          <PriorityLevelBadge priorityLevel={previewSource} />
        </Flex>
      </Flex>
      {errors.color_code && (
        <Flex justifyContent="end" width="100%">
          <Flex width={['100%', '70%']}>
            <span>{t(String(errors.color_code.message))}</span>
          </Flex>
        </Flex>
      )}

      <Flex flexDirection={['column', 'row']} alignItems="center">
        <Flex width={['100%', '30%']}>
          <FieldLabel>{t('platformPractices.fields.code')}</FieldLabel>
        </Flex>
        <Flex width={['100%', '70%']}>
          <TextField
            {...register('id_code')}
            error={t(errors.id_code?.message || '')}
          />
        </Flex>
      </Flex>

      <Flex flexDirection={['column', 'row']} alignItems="center">
        <Flex width={['100%', '30%']}>
          <FieldLabel>{t('platformPractices.fields.name')}</FieldLabel>
        </Flex>
        <Flex width={['100%', '70%']}>
          <TextField
            {...register('name')}
            error={t(errors.name?.message || '')}
          />
        </Flex>
      </Flex>

      <Flex flexDirection={['column', 'row']} alignItems="center">
        <Flex width={['100%', '30%']}>
          <FieldLabel>{t('platformPractices.fields.methodology')}</FieldLabel>
        </Flex>
        <Flex width={['100%', '70%']}>
          <Controller
            control={control}
            name="methodology_id"
            render={({ field: { onChange, value } }) => (
              <Select
                selectedOptions={methodologyOptions.find(
                  (option) => option.value === value
                )}
                onChange={(option: IOption) => onChange(option.value)}
                options={methodologyOptions}
                withoutMargin
                disabled={!!lockedMethodology}
                error={t(errors.methodology_id?.message || '')}
              />
            )}
          />
        </Flex>
      </Flex>

      <Flex flexDirection={['column', 'row']} alignItems="center">
        <Flex width={['100%', '30%']}>
          <FieldLabel>{t('platformPractices.fields.state')}</FieldLabel>
        </Flex>
        <Flex width={['100%', '70%']}>
          <Controller
            control={control}
            name="state"
            render={({ field: { onChange, value } }) => (
              <Select
                selectedOptions={stateOptions.find((option) => option.value === value)}
                onChange={(option: IOption) => onChange(option.value)}
                options={stateOptions}
                withoutMargin
                error={t(errors.state?.message || '')}
              />
            )}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export const practiceToFormDefaults = (
  practice: {
    name: string;
    id_code: string;
    color_code: string;
    state: string;
    methodology: { id: number };
  }
): IPlatformPracticeFormFields => ({
  name: practice.name,
  id_code: practice.id_code,
  color_code: practice.color_code,
  state: practice.state,
  methodology_id: String(practice.methodology.id)
});

export const buildPlatformPracticePayload = (data: IPlatformPracticeFormFields) => ({
  practice: {
    name: data.name.trim(),
    id_code: data.id_code.trim(),
    color_code: data.color_code,
    state: data.state,
    methodology_id: Number(data.methodology_id)
  }
});
